import * as React from "react";
import repository from "client/repository";
import {
    DataTable,
    DataTableBody,
    DataTableRow,
    DataTableRowColumn,
    DataTableRowHeaderColumn
} from "components/DataTable";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import PaperLayout from "components/PaperLayout";
import FormSectionHeading from "components/form/Sections/FormSectionHeading";
import ActionList from "components/ActionList/ActionList";
import {DynamicWorkerServiceResource} from "../../client/resources/dynamicWorkerServiceResource";
import {RouteComponentProps} from "react-router-dom";
import ExternalLink from "../../components/Navigation/ExternalLink";
import externalSystemLinks from "../../externalSystemLinks";
import OverflowMenu from "../../components/Menu/OverflowMenu";
import {dynamicWorkerServicesRouteLinks} from "areas/dynamicWorkerServices/dynamicWorkerServicesRouteLinks";
import buildDynamicWorkerServiceBreadcrumbTitle from "./buildDynamicWorkerServiceBreadcrumbTitle";
import SaveDialogLayout from "../../components/DialogLayout/SaveDialogLayout";
import routeLinks from "../../routeLinks";
import {InternalRedirect} from "../../components/Navigation/InternalRedirect";

interface State extends DataBaseComponentState {
    dynamicWorkerService?: DynamicWorkerServiceResource;
    redirectTo?: string;
}

type Props = RouteComponentProps<{ id: string }>;

export class DynamicWorkerServiceDetail extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    async componentDidMount() {
        const id = this.props.match.params.id;

        const dynamicWorkerService =await repository.DynamicWorkerServices.get(id);

        this.setState({
            dynamicWorkerService
        });
    }

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        const id = this.props.match.params.id;
        
        return <PaperLayout busy={this.state.busy}
                            errors={this.state.errors}
                            title="Detail"
                            breadcrumbTitle={buildDynamicWorkerServiceBreadcrumbTitle(this.state.dynamicWorkerService)}
                            sectionControl={<ActionList actions={this.getActions()}/>}>
            {this.state.dynamicWorkerService && this.getBody()}
        </PaperLayout>;
    }

    private getActions() {
        if (!this.state.dynamicWorkerService) return [];

        const overflowItems = [
            OverflowMenu.navItem("Edit", dynamicWorkerServicesRouteLinks.service(this.state.dynamicWorkerService.Id).edit),
            OverflowMenu.dialogItem("Reconfigure", this.reconfigureDialog()),
        ];

        return [
            <ExternalLink
                href={externalSystemLinks.seq.dynamicWorkerService(this.state.dynamicWorkerService.InstanceId)}>
                Seq
            </ExternalLink>,
            <ExternalLink
                href={externalSystemLinks.sumoLogic.dynamicWorkerServiceLogs(this.state.dynamicWorkerService.InstanceId)}>
                SumoLogic Logs
            </ExternalLink>,
            <ExternalLink
                href={this.state.dynamicWorkerService.Url}>
                Status
            </ExternalLink>,
            <ExternalLink 
                href={externalSystemLinks.seq.dynamicWorkerInitialLeaseTime(this.state.dynamicWorkerService.InstanceId)}>
                Initial Lease Time logs
            </ExternalLink>,
            <ExternalLink
                href={externalSystemLinks.honeycomb.dwServiceTraces(this.state.dynamicWorkerService.InstanceId)}>
                Traces
            </ExternalLink>,
            <OverflowMenu menuItems={overflowItems} />
        ];
    }

    private getBody() {
        const dynamicWorkerService = this.state.dynamicWorkerService;
        return dynamicWorkerService && <>
            <FormSectionHeading title="General"/>
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>ID</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{dynamicWorkerService.Id}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Name</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{dynamicWorkerService.Name}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Instance ID</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{dynamicWorkerService.InstanceId}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Leasing API Web App Resource ID</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{dynamicWorkerService.LeasingApiWebAppResourceId}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>URL</DataTableRowHeaderColumn>
                        <DataTableRowColumn><ExternalLink href={dynamicWorkerService.Url}>{dynamicWorkerService.Url}</ExternalLink> </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Seq Dashboard Name</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{dynamicWorkerService.SeqDashboardName}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>App Registration Audience</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{dynamicWorkerService.AppRegistrationAudience}</DataTableRowColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
        </>;
    }
    
    private reconfigureDialog() {
        return <SaveDialogLayout title={"Reconfigure"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Yes"}
                                 cancelButtonLabel={"No"}
                                 onSaveClick={() => this.reconfigure()}>
            <p>Are you sure you want to reconfigure this Dynamic Worker Service?</p>
        </SaveDialogLayout>;
    }

    private reconfigure = async (): Promise<boolean> => {
        const id = this.props.match.params.id;
        return this.doBusyTask(async () => {
            await repository.DynamicWorkerServices.reconfigure(id);
            await this.redirectToTasks(id);
        });
    }

    private redirectToTasks = async (id: string) => {
        this.setState({redirectTo: routeLinks.dynamicWorkerServices.service(id).tasks});
        return true;
    };
}

export default DynamicWorkerServiceDetail;
